import { useState } from 'react';
import { useAuthStore } from '../../auth/store';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import {
  useArchiveCharterService,
  useCharterServices,
  useCreateCharterService,
  useUpdateCharterService,
} from '../hooks/useCharter';
import { ServiceForm } from '../components/ServiceForm';
import {
  CharterHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  SelectMerchantPrompt,
  TableCard,
  TD,
  TD_MUTED,
  TH,
} from '../components/CharterPage';
import { depositRuleLabel, formatPrice } from '../components/charterFormat';
import type { CharterService, CreateServicePayload } from '../types';

export function CharterServicesPage() {
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.CHARTER_MANAGE);

  const { data: services = [], isLoading, error } = useCharterServices(selectedTenantId);
  const createService = useCreateCharterService();
  const updateService = useUpdateCharterService();
  const archiveService = useArchiveCharterService();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CharterService | null>(null);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (service: CharterService) => {
    setEditing(service);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const isSaving = createService.isPending || updateService.isPending;

  const handleSubmit = async (values: CreateServicePayload) => {
    if (editing) {
      await updateService.mutateAsync({ id: editing.id, data: values });
    } else {
      await createService.mutateAsync(values);
    }
    closeModal();
  };

  const handleArchive = (service: CharterService) => {
    if (window.confirm(`Archive "${service.name}"? Existing bookings keep their snapshot.`)) {
      archiveService.mutate(service.id);
    }
  };

  return (
    <div className="py-6 max-w-6xl mx-auto px-2">
      <CharterHeader
        title="Charter · Services"
        subtitle="Your bookable service menu. Price 0 = free; deposit rule can be none, a percentage, or full upfront."
        action={
          canManage && selectedTenantId ? (
            <Button size="sm" className="h-[40px]" onClick={openCreate}>
              New service
            </Button>
          ) : undefined
        }
      />

      {!selectedTenantId ? (
        <SelectMerchantPrompt what="their Charter services" />
      ) : isLoading ? (
        <LoadingState label="Loading services…" />
      ) : error ? (
        <ErrorState label="Failed to load services." />
      ) : services.length === 0 ? (
        <EmptyState label={`No services yet.${canManage ? ' Click "New service" to create one.' : ''}`} />
      ) : (
        <TableCard>
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className={TH}>Name</th>
                <th className={TH}>Duration</th>
                <th className={TH}>Price</th>
                <th className={TH}>Deposit</th>
                {canManage && <th className={TH}>Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {services.map((s) => (
                <tr key={s.id} className="hover:bg-muted transition-colors">
                  <td className={`${TD} font-medium`}>{s.name}</td>
                  <td className={TD_MUTED}>{s.durationMinutes} min</td>
                  <td className={TD}>{formatPrice(s.price, s.currency)}</td>
                  <td className={TD_MUTED}>{depositRuleLabel(s.depositRule)}</td>
                  {canManage && (
                    <td className="px-6 py-4 space-x-4">
                      <button onClick={() => openEdit(s)} className="text-xs text-blue-600 hover:underline">
                        Edit
                      </button>
                      <button onClick={() => handleArchive(s)} className="text-xs text-red-600 hover:underline">
                        Archive
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </TableCard>
      )}

      <Modal isOpen={modalOpen} onClose={closeModal} size="md">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-foreground">{editing ? 'Edit service' : 'New service'}</h2>
            <button onClick={closeModal} className="text-muted-foreground hover:text-foreground text-2xl leading-none">
              &times;
            </button>
          </div>
          <ServiceForm initial={editing} onSubmit={handleSubmit} onCancel={closeModal} isSaving={isSaving} />
        </div>
      </Modal>
    </div>
  );
}
